import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Clock3, LoaderCircle, PlayCircle, RotateCcw } from "lucide-react";
import Swal from "sweetalert2";

import { extractErrorMessage } from "../../../../../api";
import {
    fetchLearnerQuizCurrentApi,
    fetchLearnerQuizHistoryApi,
    fetchLearnerQuizReviewApi,
    startLearnerQuizApi,
    submitLearnerQuizApi,
} from "../../../../services/moduleQuizApi";
import QuestionRenderer from "./QuestionRenderer";
import QuizReviewPanel from "./QuizReviewPanel";
import QuizSubmitSummary from "./QuizSubmitSummary";
import "./QuizRunner.css";

const SUPPORTED_TYPES = new Set(["QCU", "QCM", "OUI_NON", "COMPLETEMENT", "DISSERTATION", "MATCHING", "CLASSEMENT", "GAMING"]);
const isSupportedV1 = (question) => SUPPORTED_TYPES.has(question?.typeQuestion);

const createInitialAnswers = (questions = []) =>
    questions.reduce((accumulator, question) => {
        if (question.typeQuestion === "QCM" || question.typeQuestion === "CLASSEMENT") {
            accumulator[question.id] = Array.isArray(question.submittedResponseJson)
                ? question.submittedResponseJson.map(String)
                : question.typeQuestion === "CLASSEMENT"
                    ? (question.reponsesPossibles || []).map((option) => String(option.id))
                    : [];
            return accumulator;
        }

        if (question.typeQuestion === "MATCHING") {
            accumulator[question.id] = question.submittedResponseJson && typeof question.submittedResponseJson === "object"
                ? question.submittedResponseJson
                : {};
            return accumulator;
        }

        if (question.typeQuestion === "GAMING") {
            accumulator[question.id] = question.submittedResponseJson && typeof question.submittedResponseJson === "object"
                ? question.submittedResponseJson
                : "";
            return accumulator;
        }

        if (question.submittedResponsePossibleId != null) {
            accumulator[question.id] = String(question.submittedResponsePossibleId);
            return accumulator;
        }

        if (typeof question.submittedResponseJson === "string") {
            accumulator[question.id] = question.submittedResponseJson;
            return accumulator;
        }

        accumulator[question.id] = question.typeQuestion === "QCM" ? [] : "";
        return accumulator;
    }, {});

const isAnswered = (question, value) => {
    if (!isSupportedV1(question)) return true;
    if (question.typeQuestion === "QCM") return Array.isArray(value) && value.length > 0;
    if (question.typeQuestion === "CLASSEMENT") {
        return Array.isArray(value) && value.length === (question.reponsesPossibles || []).length;
    }
    if (question.typeQuestion === "MATCHING") {
        const leftItems = question.configJson?.col_gauche || [];
        return leftItems.length > 0 && leftItems.every((item) => String(value?.[item] || "").trim().length > 0);
    }
    if (question.typeQuestion === "GAMING") {
        return Boolean(value?.selectedOptionId || value);
    }
    return String(value || "").trim().length > 0;
};

const buildAnswerPayload = (question, value) => {
    switch (question.typeQuestion) {
        case "QCU":
        case "OUI_NON":
            return {
                questionId: question.id,
                reponsePossibleId: value ? Number(value) : null,
                dureeReponseS: 0,
            };
        case "QCM":
            return {
                questionId: question.id,
                reponseJson: (Array.isArray(value) ? value : []).map((item) => Number(item)),
                dureeReponseS: 0,
            };
        case "COMPLETEMENT":
        case "DISSERTATION":
            return {
                questionId: question.id,
                reponseJson: String(value || ""),
                dureeReponseS: 0,
            };
        case "MATCHING":
            return {
                questionId: question.id,
                reponseJson: value && typeof value === "object" ? value : {},
                dureeReponseS: 0,
            };
        case "CLASSEMENT":
            return {
                questionId: question.id,
                reponseJson: (Array.isArray(value) ? value : []).map((item) => Number(item)),
                dureeReponseS: 0,
            };
        case "GAMING": {
            const selectedOptionId = value?.selectedOptionId ?? value;
            const selectedOption = (question.reponsesPossibles || []).find((option) => String(option.id) === String(selectedOptionId));
            return {
                questionId: question.id,
                reponseJson: {
                    selectedOptionId: selectedOptionId ? Number(selectedOptionId) : null,
                    correct: Boolean(selectedOption?.correct),
                },
                dureeReponseS: 0,
            };
        }
        default:
            return {
                questionId: question.id,
                dureeReponseS: 0,
            };
    }
};

export default function QuizRunner({ moduleId, content, onRefreshPlayer }) {
    const [currentData, setCurrentData] = useState(null);
    const [historyData, setHistoryData] = useState(null);
    const [activeAttemptData, setActiveAttemptData] = useState(null);
    const [answers, setAnswers] = useState({});
    const [review, setReview] = useState(null);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const requestSeqRef = useRef(0);
    const currentContentIdRef = useRef(content?.id);

    const contentId = content?.id;
    currentContentIdRef.current = contentId;
    const questions = useMemo(
        () => activeAttemptData?.questions || currentData?.questions || [],
        [activeAttemptData?.questions, currentData?.questions]
    );
    const supportedQuestions = useMemo(
        () => questions.filter((question) => isSupportedV1(question)),
        [questions]
    );

    const refreshData = useCallback(async () => {
        if (!moduleId || !contentId) return;
        const requestSeq = ++requestSeqRef.current;
        setLoading(true);
        setError(null);
        try {
            const [currentResponse, historyResponse] = await Promise.all([
                fetchLearnerQuizCurrentApi(moduleId, contentId),
                fetchLearnerQuizHistoryApi(moduleId, contentId),
            ]);

            const currentPayload = currentResponse.data;
            if (requestSeq !== requestSeqRef.current) return;
            setCurrentData(currentPayload);
            setHistoryData(historyResponse.data);

            if (currentPayload?.currentAttempt) {
                setActiveAttemptData(currentPayload);
                setAnswers(createInitialAnswers(currentPayload.questions));
            } else {
                setActiveAttemptData(null);
            }
        } catch (requestError) {
            if (requestSeq !== requestSeqRef.current) return;
            setError(extractErrorMessage(requestError, "Impossible de charger le quiz apprenant."));
        } finally {
            if (requestSeq === requestSeqRef.current) {
                setLoading(false);
            }
        }
    }, [contentId, moduleId]);

    useEffect(() => {
        requestSeqRef.current += 1;
        setCurrentData(null);
        setHistoryData(null);
        setActiveAttemptData(null);
        setAnswers({});
        setReview(null);
        setResult(null);
        setError(null);
        setActionLoading(false);
        setLoading(true);
        refreshData();
    }, [contentId, moduleId, refreshData]);

    const handleStart = async () => {
        const startContentId = contentId;
        const requestSeq = ++requestSeqRef.current;
        setActionLoading(true);
        setError(null);
        try {
            const response = await startLearnerQuizApi(moduleId, contentId);
            if (requestSeq !== requestSeqRef.current) return;
            setActiveAttemptData(response.data);
            setAnswers(createInitialAnswers(response.data.questions));
            setResult(null);
            setReview(null);
            await refreshData();
        } catch (requestError) {
            if (requestSeq !== requestSeqRef.current) return;
            setError(extractErrorMessage(requestError, "Impossible de demarrer le quiz."));
        } finally {
            if (String(currentContentIdRef.current) === String(startContentId)) {
                setActionLoading(false);
            }
        }
    };

    const handleAnswerChange = (questionId, value) => {
        setAnswers((previous) => ({
            ...previous,
            [questionId]: value,
        }));
    };

    const handleSubmit = async () => {
        const submitContentId = contentId;
        const tentativeId = activeAttemptData?.attempt?.tentativeId
            ?? activeAttemptData?.currentAttempt?.tentativeId
            ?? currentData?.currentAttempt?.tentativeId;

        if (!tentativeId) {
            setError("Impossible de soumettre le quiz : tentative introuvable.");
            return;
        }

        const missingQuestions = supportedQuestions.filter((question) => !isAnswered(question, answers[question.id]));
        if (missingQuestions.length) {
            setError("Merci de répondre à toutes les questions prises en charge avant la soumission.");
            return;
        }

        setActionLoading(true);
        setError(null);
        try {
            const payload = {
                tentativeId,
                answers: supportedQuestions.map((question) => buildAnswerPayload(question, answers[question.id])),
            };
            const response = await submitLearnerQuizApi(moduleId, submitContentId, {
                ...payload,
            });

            if (String(currentContentIdRef.current) !== String(submitContentId)) return;
            setResult(response.data);
            setActiveAttemptData(null);
            await refreshData();
            if (typeof onRefreshPlayer === "function") {
                onRefreshPlayer();
            }
            await Swal.fire({
                icon: response.data.success ? "success" : response.data.correctionPending ? "info" : "warning",
                text: response.data.message,
                timer: 1800,
                showConfirmButton: false,
            });
        } catch (requestError) {
            if (String(currentContentIdRef.current) !== String(submitContentId)) return;
            setError(extractErrorMessage(requestError, "Impossible de soumettre le quiz."));
        } finally {
            if (String(currentContentIdRef.current) === String(submitContentId)) {
                setActionLoading(false);
            }
        }
    };

    const handleOpenReview = async (tentativeId) => {
        setActionLoading(true);
        setError(null);
        try {
            const response = await fetchLearnerQuizReviewApi(moduleId, contentId, tentativeId);
            setReview(response.data);
        } catch (requestError) {
            setError(extractErrorMessage(requestError, "Impossible de charger la relecture de la tentative."));
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="qr-state">
                <LoaderCircle className="qr-spin" size={18} />
                Chargement du quiz...
            </div>
        );
    }

    if (error) {
        return <div className="qr-state qr-state--error">{error}</div>;
    }

    const attemptsRemaining = currentData?.attemptsRemaining ?? historyData?.attemptsRemaining ?? 0;
    const canStart = Boolean(currentData?.canStart) && !actionLoading;
    const activeAttempt = activeAttemptData?.attempt || currentData?.currentAttempt;
    const latestAttemptExpired = String(currentData?.latestAttempt?.tentativeStatus || "").toUpperCase() === "EXPIRE";

    return (
        <div className="qr-shell">
            <div className="qr-header">
                <div>
                    <span className="qr-eyebrow">Quiz ModuleCle</span>
                    <h3>{content.quizLabel || currentData?.quiz?.titre || "Quiz"}</h3>
                    <p>{currentData?.quiz?.description || content.description || "Passez votre quiz directement depuis le player apprenant."}</p>
                </div>
                <div className="qr-stats">
                    <span><Clock3 size={14} /> {currentData?.quiz?.dureeMin || "--"} min</span>
                    <span><CheckCircle2 size={14} /> Seuil: {currentData?.quiz?.seuilReussite || "--"}%</span>
                    <span><RotateCcw size={14} /> Restantes: {attemptsRemaining}</span>
                </div>
            </div>

            {review ? <QuizReviewPanel review={review} onClose={() => setReview(null)} /> : null}

            {result ? (
                <QuizSubmitSummary
                    result={result}
                    canRetry={result.attemptsRemaining > 0 && !result.correctionPending}
                    onRetry={handleStart}
                    onReview={result.attempt?.tentativeId ? () => handleOpenReview(result.attempt.tentativeId) : null}
                />
            ) : null}

            {activeAttempt ? (
                <div className="qr-attempt-card">
                    <div className="qr-attempt-header">
                        <strong>Tentative #{activeAttempt.numTentative}</strong>
                        <p>{currentData?.currentAttempt ? "Tentative en cours" : "Quiz démarré"}</p>
                    </div>

                    <div className="qr-progress-line">
                        <CheckCircle2 size={16} />
                        <span>{questions.filter((question) => isAnswered(question, answers[question.id])).length} / {questions.length} questions renseignées</span>
                    </div>

                    <div className="qr-questions">
                        {questions.map((question) => (
                            <article key={question.id} className="qr-question-card">
                                <div className="qr-question-head">
                                    <span>Question {question.ordre}</span>
                                    <strong>{question.enonce}</strong>
                                </div>
                                <QuestionRenderer
                                    question={question}
                                    value={answers[question.id]}
                                    onChange={(value) => handleAnswerChange(question.id, value)}
                                    disabled={actionLoading}
                                />
                            </article>
                        ))}
                    </div>

                    <div className="qr-inline-actions">
                        <button className="cr-btn cr-btn--primary" onClick={handleSubmit} disabled={actionLoading}>
                            {actionLoading ? "Soumission..." : "Soumettre le quiz"}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="qr-start-card">
                    <div className="qr-start-state">
                        <PlayCircle size={22} />
                        <div>
                            <strong>{currentData?.maxAttemptsReached ? "Nombre maximal de tentatives atteint" : "Prêt à lancer le quiz"}</strong>
                            <p>
                                {latestAttemptExpired && canStart
                                    ? "Votre dernière tentative a expiré. Vous pouvez recommencer ce quiz."
                                    : currentData?.maxAttemptsReached
                                    ? "Vous ne pouvez plus démarrer une nouvelle tentative pour ce quiz."
                                    : "Vous pouvez commencer une nouvelle tentative ou revoir votre historique."}
                            </p>
                        </div>
                    </div>

                    <div className="qr-inline-actions">
                        <button className="cr-btn cr-btn--primary" onClick={handleStart} disabled={!canStart}>
                            {latestAttemptExpired ? "Recommencer le quiz" : "Commencer / reprendre le quiz"}
                        </button>
                        {/* 
                        {currentData?.latestAttempt?.tentativeId ? (
                            <button className="cr-btn cr-btn--secondary" onClick={() => handleOpenReview(currentData.latestAttempt.tentativeId)}>
                                Revoir la dernière tentative
                            </button>
                        ) : null}
                        */}
                    </div>
                </div>
            )}

            <div className="qr-history-card">
                <h4>Historique des tentatives</h4>
                {(historyData?.attempts || []).length ? (
                    <div className="qr-history-list">
                        {historyData.attempts.map((attempt) => {
                            const status = String(attempt.bridgeStatus || "").toUpperCase();
                            const isSuccess = status === "SUCCESS" || status === "PASSED";
                            const isFailed = status === "FAILED";
                            const isInProgress = status === "IN_PROGRESS" || status === "STARTED";
                            
                            return (
                                <div key={attempt.tentativeId} className="qr-history-item">
                                    <div>
                                        <strong>Tentative #{attempt.numTentative}</strong>
                                        <p>Date: {attempt.dateTentative || "--"}</p>
                                    </div>
                                    <div>
                                        <span className={`qr-badge ${isSuccess ? "qr-badge--success" : isFailed ? "qr-badge--failed" : isInProgress ? "qr-badge--progress" : "qr-badge--neutral"}`}>
                                            {attempt.bridgeStatus || "Inconnu"}
                                        </span>
                                    </div>
                                    <div>
                                        <strong>{attempt.score ?? "--"}%</strong>
                                    </div>
                                    {/* 
                                    {attempt.reviewAvailable ? (
                                        <button className="cr-btn cr-btn--secondary" onClick={() => handleOpenReview(attempt.tentativeId)}>
                                            Relecture
                                        </button>
                                    ) : null}
                                    */}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="qr-empty">Aucune tentative enregistrée pour le moment.</p>
                )}
            </div>
        </div>
    );
}
